import {Component, computed, inject, OnInit, signal, ChangeDetectionStrategy} from "@angular/core"
import {RouterOutlet} from "@angular/router"
import {CreateItemComponent} from "@common/components/base/create-item.component"
import {AddTemplateDialogComponent} from "@app/templates/add-template-dialog/add-template-dialog.component"
import {CheckboxesFilterComponent} from "@common/components/filters/checkboxes-filter/checkboxes-filter.component"
import {TagSearchFilterComponent} from "@common/components/filters/tag-search-filter/tag-search-filter.component"
import {ItemFiltersComponent} from "@common/components/item/item-filters/item-filters.component"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {SidebarLayoutComponent} from "@platform/components/layouts/sidebar-layout/sidebar-layout.component"
import {TemplateType} from "@generated"
import {Enums} from "@enums"
import {Labels, ProductTypeLabels, SceneTypeLabels, TemplateTypeLabels} from "@labels"
import {TemplatesGridComponent} from "@app/templates/templates-grid/templates-grid.component"
import {FiltersService} from "@common/services/filters/filters.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-templates-page",
    imports: [SidebarLayoutComponent, CheckboxesFilterComponent, TagSearchFilterComponent, ItemFiltersComponent, RouterOutlet, TemplatesGridComponent],
    templateUrl: "./templates-page.component.html",
    styleUrl: "./templates-page.component.scss",
})
export class TemplatesPageComponent extends CreateItemComponent<AddTemplateDialogComponent> implements OnInit {
    readonly Enums = Enums
    readonly Labels = Labels

    public readonly $viewMode = signal<"scenes" | "products" | undefined>(undefined)
    readonly filters = inject(FiltersService)
    readonly permission = inject(PermissionsService)
    readonly organizations = inject(OrganizationsService)
    $can = this.permission.$to

    public readonly $entityName = computed(() => {
        const viewMode = this.$viewMode()
        if (viewMode === "scenes") return "scene"
        else if (viewMode === "products") return "product"
        else return "template"
    })

    readonly $templateType = computed(() => {
        if (this.$viewMode() === "scenes") return TemplateType.Scene
        else return TemplateType.Product
    })

    public readonly $typeLabels = computed(() => {
        const viewMode = this.$viewMode()
        if (viewMode === "scenes") return SceneTypeLabels
        else if (viewMode === "products") return ProductTypeLabels
        else return TemplateTypeLabels
    })

    ngOnInit() {
        this.$viewMode.set(this.route.snapshot.data.viewMode)
    }

    protected openCreateDialog() {
        const templateType = this.$templateType()
        const dialogRef = this.dialog.open(AddTemplateDialogComponent, {
            data: {templateType},
        })
        this.setDialogRef(dialogRef)
    }

    protected override handleDialogResult(result: unknown): boolean | void {
        if (result && typeof result === "object" && "id" in result) {
            const typedResult = result as {id: string}
            if (typedResult.id) {
                // Navigate to the created template
                void this.router.navigate([typedResult.id, "revisions", "add"], {
                    relativeTo: this.route,
                    queryParamsHandling: "preserve",
                })
                // Return false to skip default navigation
                return false
            }
        }

        // Return undefined/void for default behavior
    }
}
